import React from "react";
import { Link } from "react-router-dom";
import { apiService } from "../../../../../api/api";
import { ROUTES } from "../../../../../config/routes";
import { WidgetComponentProps } from "../../types";
import { useWidgetData } from "../../useWidgetData";
import { BarItem, CardError, CardLoading, IssueBars, Kpi, KpiGrid } from "../../ui";

interface UsersData {
  active: number;
  inactive: number;
  noTools: number;
  topTools: BarItem[];
}

/** Tools may arrive as an array or a JSON string (same handling as AppManagment.tsx). */
const parseTools = (tools: unknown): string[] => {
  if (Array.isArray(tools)) return tools.filter((t): t is string => typeof t === "string");
  if (typeof tools === "string") {
    try {
      const parsed = JSON.parse(tools);
      return Array.isArray(parsed) ? parsed.filter((t): t is string => typeof t === "string") : [];
    } catch {
      return [];
    }
  }
  return [];
};

const AdminUsersCard: React.FC<WidgetComponentProps> = () => {
  const { data, loading, error, reload } = useWidgetData<UsersData>("admin-users", async () => {
    const [usersRes, toolsRes] = await Promise.all([
      apiService.users.getAllUsers("all"),
      apiService.users.getValidTools().catch(() => null),
    ]);
    const users: Array<Record<string, unknown>> = Array.isArray(usersRes?.data) ? usersRes.data : [];
    const toolLabels = new Map((toolsRes?.tools ?? []).map((t) => [t.value, t.label]));

    const toolCounts = new Map<string, number>();
    let active = 0;
    let noTools = 0;
    users.forEach((u) => {
      if (u.active !== true) return;
      active += 1;
      const tools = parseTools(u.tools);
      if (tools.length === 0) noTools += 1;
      tools.forEach((t) => toolCounts.set(t, (toolCounts.get(t) ?? 0) + 1));
    });

    const topTools = Array.from(toolCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([tool, count]) => ({ label: toolLabels.get(tool) ?? tool, count, to: ROUTES.ADMIN_TOOLS }));

    return { active, inactive: users.length - active, noTools, topTools };
  });

  if (loading && !data) return <CardLoading />;
  if (error) return <CardError message={error} onRetry={reload} />;
  if (!data) return null;

  return (
    <>
      <KpiGrid columns={3}>
        <Kpi label="Active users" value={data.active} tone="info" to={ROUTES.ADMIN_TOOLS} emphasis />
        <Kpi label="Inactive users" value={data.inactive} to={ROUTES.ADMIN_TOOLS} />
        <Kpi
          label="No tools"
          value={data.noTools}
          tone={data.noTools > 0 ? "warn" : "neutral"}
          hint="Active users without access"
          to={ROUTES.ADMIN_TOOLS}
        />
      </KpiGrid>

      <IssueBars title="Top tools (active users)" items={data.topTools} emptyText="No tool access granted yet" />

      <div className="home-card__meta">
        <Link className="home-link" to={ROUTES.ADMIN_TOOLS}>
          Manage users
        </Link>
      </div>
    </>
  );
};

export default AdminUsersCard;
